import { Link } from "react-router-dom";
import { Archive, ChevronRight } from "lucide-react";
import { useGroup } from "@/lib/GroupContext";
import { STEP_LABELS } from "@/lib/api";
import { Tag } from "@/components/Brand";

const TASK_TAB = { tape: "tape", crosscheck: "crosscheck", clue: "clue", profile: "profile", secret: "interrogate" };
const TASK_NAME = {
  tape: "Listen to the cassette",
  crosscheck: "Cross-check the record",
  clue: "Recover the hidden evidence",
  profile: "Read the suspect profile",
  secret: "Reveal what they're hiding",
};
const status = (p) => {
  const n = STEP_LABELS.filter(([k]) => p[k]).length;
  return n === 0 ? "todo" : n === STEP_LABELS.length ? "done" : "progress";
};

function SuspectTile({ sid, idx }) {
  const { state } = useGroup();
  const s = state.suspects[sid];
  return (
    <section className="panel flex flex-col rise" style={{ animationDelay: `${idx * 70}ms` }} data-testid={`station-card-${sid}`}>
      <Link to={`/suspects/${sid}`} className="tile !border-x-0 !border-t-0" data-testid={`open-station-${sid}`}>
        <span className="flex items-center gap-5">
          <span className="suspect-photo" aria-hidden>{s.initials}</span>
          <span>
            <span className="block text-2xl font-black text-[#0033A1] leading-tight" data-testid={`station-card-${sid}-name`}>{s.name}</span>
            <span className="block text-[17px] text-[#505a5f] mt-1">{s.role}</span>
            <span className="block mt-3"><Tag status={status(s.progress)} testid={`station-status-${sid}`} /></span>
          </span>
        </span>
        <ChevronRight size={30} className="chev shrink-0" />
      </Link>
      <ul className="flex-1">
        {STEP_LABELS.map(([step]) => (
          <li key={step} className="task-row" data-testid={`tick-${sid}-${step}`} data-done={s.progress[step] ? "true" : "false"}>
            <Link to={`/suspects/${sid}?tab=${TASK_TAB[step]}`} className="link">{TASK_NAME[step]}</Link>
            <Tag status={s.progress[step] ? "done" : "todo"} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChargePanel() {
  const { state } = useGroup();
  return (
    <div className="panel p-6" data-testid="hub-accusation-panel">
      <h2 className="text-2xl font-black">Charge decision</h2>
      {state.verdict ? (
        <>
          <p className="text-lg mt-3">You charged {state.verdict.suspect_name}.</p>
          <Link to="/charge" className="btn-spore mt-5 w-full" data-testid="hub-view-verdict-button">View the outcome</Link>
        </>
      ) : state.accusation_unlocked ? (
        <>
          <p className="text-lg mt-3">All four pieces of hidden evidence are recovered. You may make one charge decision.</p>
          <Link to="/charge" className="btn-spore mt-5 w-full" data-testid="hub-make-accusation-button">Make your charge decision</Link>
        </>
      ) : (
        <div className="warning-text mt-4" data-testid="accusation-locked-message">
          <span className="warning-icon" aria-hidden>!</span>
          <strong>Unlock all four hidden clues to make your accusation ({state.clues_unlocked}/4)</strong>
        </div>
      )}
    </div>
  );
}

function Notes() {
  const { state } = useGroup();
  return (
    <div className="panel p-6" data-testid="case-notes">
      <h2 className="text-2xl font-black">Case notes</h2>
      {state.notes.length === 0 && <p className="text-lg text-[#505a5f] mt-3">Nothing on file yet. Secrets you uncover will be recorded here.</p>}
      <ol className="mt-4 space-y-5 max-h-[60vh] overflow-y-auto thin-scroll pr-2">
        {state.notes.map((n) => (
          <li key={n.id} className={`note ${n.kind === "secret" ? "note-secret" : ""}`} data-testid={`case-note-${n.suspect}-${n.kind}`}>
            <div className="font-bold text-[17px]">{n.title}</div>
            {n.kind === "secret" && <p className="text-[17px] mt-1 leading-relaxed">{n.text}</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function Home() {
  const { state, setLockerOpen } = useGroup();
  return (
    <div data-testid="home-page">
      <section className="hero hero-bleed" data-testid="home-hero">
        <div className="py-14 max-w-[1100px]">
          <p className="text-[19px] font-bold opacity-90">Operation SPORE · Major investigation</p>
          <h1 className="text-5xl lg:text-6xl font-black mt-3 leading-[1.08]" data-testid="hub-title">The death of Dr Ebaro Monroe</h1>
          <p className="text-xl mt-6 leading-relaxed opacity-95 max-w-3xl">
            Dr Monroe died in Chamber C3 of the old asylum. Four scientists were in the building. Interview them in any order,
            test their accounts against the records and make a charge decision.
          </p>
          <div className="flex gap-4 mt-8">
            <Link to="/briefing" className="btn-onblue" data-testid="hub-briefing-link">Read the briefing</Link>
            <button className="btn-onblue" onClick={() => setLockerOpen(true)} data-testid="hub-evidence-locker-button"><Archive size={18} /> Submit evidence reference</button>
          </div>
        </div>
      </section>
      {!state ? (
        <p className="py-10 text-lg" data-testid="hub-loading">Opening case file…</p>
      ) : (
        <div className="grid grid-cols-12 gap-10 mt-12" data-testid="case-hub">
          <div className="col-span-12 xl:col-span-8">
            <h2 className="text-4xl font-black">Suspect interviews</h2>
            <p className="text-lg text-[#505a5f] mt-2 mb-6">Choose any suspect. There is no set order.</p>
            <div className="grid grid-cols-1 2xl:grid-cols-2 auto-rows-fr gap-6" data-testid="suspect-tiles">
              {state.order.map((sid, i) => <SuspectTile key={sid} sid={sid} idx={i} />)}
            </div>
          </div>
          <aside className="col-span-12 xl:col-span-4 space-y-6 xl:pt-[92px]">
            <ChargePanel />
            <div className="panel p-6">
              <h2 className="text-2xl font-black">Evidence recovered</h2>
              <div className="text-5xl font-black text-[#0033A1] mt-3" data-testid="hub-clue-count">{state.clues_unlocked}/4</div>
              <p className="text-lg text-[#505a5f]">hidden documents submitted</p>
            </div>
            <Notes />
          </aside>
        </div>
      )}
    </div>
  );
}
